// src/lib/firebase.js
import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  setPersistence,
  browserLocalPersistence,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import {
  getFirestore,
  setLogLevel,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported as isMessagingSupported,
} from "firebase/messaging";
import { doc } from "firebase/firestore";

/* ---------------- Firebase config ---------------- */
const env = import.meta?.env || {};
const currentHost = typeof window !== "undefined" ? window.location.hostname : "";

  const getCurrentAuthDomain = () => {
    // Use firebase hosting domains for local/dev where custom domain may not be authorized
    const isLocal = (
      currentHost === "localhost" ||
      currentHost === "127.0.0.1" ||
      currentHost === "0.0.0.0" ||
      currentHost === "[::1]"
    );
    if (isLocal) return "ppac-web.firebaseapp.com";

    if (currentHost === "merkpres.com" || currentHost === "www.merkpres.com") return "merkpres.com";
    if (currentHost === "ppac-web.firebaseapp.com") return "ppac-web.firebaseapp.com";
    if (currentHost === "ppac-web.web.app") return "ppac-web.web.app";

    // Fallback to Firebase Hosting domain to minimize unauthorized-domain issues
    return "ppac-web.firebaseapp.com";
  };

  const firebaseConfig = {
    apiKey: env.VITE_FIREBASE_API_KEY || "AIzaSyD8rRf_X_zyaav6OZmnauPjnRWJ6hr3TW8",
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN || getCurrentAuthDomain(),
    projectId: env.VITE_FIREBASE_PROJECT_ID || "ppac-web",
    // Use correct Firebase Storage bucket name
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET || "ppac-web.firebasestorage.app",
    messagingSenderId: env.VITE_FIREBASE_SENDER_ID || "702419735324",
    appId: env.VITE_FIREBASE_APP_ID || "1:702419735324:web:688939e74ad7b8aa9820d9"
  };

const app = getApps().length ? getApp() : initializeApp(firebaseConfig);

/* ---------------- Core services ---------------- */
export const auth = getAuth(app);
auth.useDeviceLanguage?.();

// Set persistence after auth is initialized
try {
  setPersistence(auth, browserLocalPersistence);
} catch (error) {
  console.warn("Failed to set auth persistence:", error);
}

// DEV ONLY: enable to test with Firebase Console "test phone numbers" (bypasses reCAPTCHA/SMS)
if (import.meta.env.DEV && import.meta.env.VITE_FIREBASE_DISABLE_APP_VERIFICATION === "true") {
  // eslint-disable-next-line no-unused-expressions
  auth.settings && (auth.settings.appVerificationDisabledForTesting = true);
}

// TEMPORARY: Enable test mode to bypass rate limiting
if (import.meta.env.DEV) {
  // eslint-disable-next-line no-unused-expressions
  auth.settings && (auth.settings.appVerificationDisabledForTesting = true);
}

export const db = getFirestore(app);

// Be explicit about the bucket (avoids odd defaults)
export const storage = getStorage(app, "gs://ppac-web.firebasestorage.app");
// Updated to use asia-southeast1 region (cache-bust: 2025-01-09)
export const functions = getFunctions(app, "asia-southeast1");


/* ---------------- Google provider ---------------- */
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope("profile");
googleProvider.addScope("email");

// Set custom parameters for popup authentication
googleProvider.setCustomParameters({
  prompt: 'select_account',
  display: 'popup'
});

/* ---------------- Phone auth helpers ---------------- */
export async function signInWithPhone(phoneNumber, appVerifier) {
  try {
    // Let Firebase handle the invisible reCAPTCHA lifecycle
    const confirmation = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
    return confirmation.verificationId;
  } catch (error) {
    throw error;
  }
}

export async function verifyPhoneCode(verificationId, code) {
  const cred = PhoneAuthProvider.credential(verificationId, code);
  return await signInWithCredential(auth, cred);
}


/* ---------------- Google sign-in helpers ---------------- */
// Back-compat helper used by some pages: delegate to popup flow with redirect fallback
export async function beginGoogleSignIn() {
  return await signInWithGoogle();
}

export async function handleGoogleRedirectResult() {
  try {
    console.log("Attempting to get redirect result...");
    console.log("Auth app:", auth.app.name);
    console.log("Auth domain:", auth.app.options.authDomain);
    console.log("Current URL:", window.location.href);
    
    // Check if we have auth-related URL parameters that suggest a redirect
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthParams = urlParams.has('code') || urlParams.has('state') || 
                         window.location.href.includes('__/auth/');
    
    console.log("Has auth parameters:", hasAuthParams);
    
    if (!hasAuthParams) {
      console.log("No auth parameters found, no redirect result expected");
      return null;
    }
    
    // Wait longer for Firebase Auth to be fully ready and process the redirect
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // call once after app loads to complete the sign-in
    const result = await getRedirectResult(auth);
    console.log("getRedirectResult returned:", result ? "Success" : "Null");
    
    if (result) {
      console.log("Redirect result user:", result.user?.email);
      console.log("Redirect result credential:", result.credential?.providerId);
    } else {
      console.warn("Auth parameters found but no redirect result - redirect may have been consumed");
    }
    
    return result; // may be null if no redirect happened
  } catch (error) {
    console.error("Error getting redirect result:", error);
    throw error;
  }
}

// Google Sign-In using popup (more reliable for web)
export async function signInWithGoogle() {
  try {
    console.log("Starting Google Sign-In popup...");
    console.log("Current domain:", window.location.hostname);
    console.log("Auth domain:", auth.app.options.authDomain);
    console.log("Provider scopes:", googleProvider.getScopes());
    console.log("Provider custom params:", googleProvider.getCustomParameters());
    
    // Check if popups are likely to be blocked
    const popupTest = window.open('', '_blank', 'width=1,height=1');
    if (!popupTest || popupTest.closed || typeof popupTest.closed == 'undefined') {
      console.warn("Popups appear to be blocked");
      throw new Error("Popup blocked. Please allow popups for this site and try again.");
    }
    popupTest.close();
    
    // Use popup-based authentication for better reliability on web
    console.log("Opening popup window...");
    const result = await signInWithPopup(auth, googleProvider);
    console.log("Google Sign-In successful:", result.user);
    console.log("User email:", result.user.email);
    console.log("User display name:", result.user.displayName);
    return result;
  } catch (error) {
    console.error("Google Sign-In popup error:", error);
    console.error("Error code:", error.code);
    console.error("Error message:", error.message);
    
    // Provide clear error messages for different failure scenarios
    if (error.code === 'auth/popup-blocked' || error.message.includes("Popup blocked")) {
      console.log("Popup blocked, falling back to redirect authentication...");
      // Fall back to redirect authentication if popup is blocked
      await signInWithRedirect(auth, googleProvider);
      return null; // Will be handled by redirect result
    } else if (error.code === 'auth/popup-closed-by-user') {
      throw new Error("Sign-in cancelled. Please try again.");
    } else if (error.code === 'auth/account-exists-with-different-credential') {
      throw new Error("An account already exists with this email. Please use a different sign-in method.");
    } else if (error.code === 'auth/email-already-in-use') {
      throw new Error("This email is already registered. Please sign in instead.");
    } else if (error.code === 'auth/operation-not-allowed') {
      throw new Error("Google Sign-In is not enabled. Please contact support.");
    } else if (error.code === 'auth/unauthorized-domain') {
      throw new Error("This domain is not authorized for Google Sign-In.");
    } else if (error.code === 'auth/network-request-failed') {
      throw new Error("Network error. Please check your internet connection and try again.");
    } else if (error.code === 'auth/timeout') {
      throw new Error("Sign-in timed out. Please try again.");
    } else {
      throw new Error(`Google sign-in failed: ${error.message}`);
    }
  }
}

/* ---------------- Optional: Messaging ---------------- */
export async function initMessaging(onForegroundMessage) {
  try {
    if (!(await isMessagingSupported())) return null;
    const messaging = getMessaging(app);
    const vapidKey = (import.meta?.env?.VITE_FIREBASE_VAPID_KEY || "").trim();
    if (!vapidKey) return null;
    const token = await getToken(messaging, { vapidKey });
    if (typeof onForegroundMessage === "function") {
      onMessage(messaging, (payload) => onForegroundMessage(payload));
    }
    return token;
  } catch {
    return null;
  }
}

/* ---------------- Debug (optional) ---------------- */
try {
  if (import.meta?.env?.DEV) setLogLevel("error");
} catch {}

/* ---------------- Global Error Handling ---------------- */
// Handle unhandled Firebase errors
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason?.message?.includes('firestore') || 
        event.reason?.message?.includes('Firestore') ||
        event.reason?.code === 'unavailable' ||
        event.reason?.code === 'permission-denied') {
      console.warn('Unhandled Firebase error caught:', event.reason);
      // Prevent the error from showing in console
      event.preventDefault();
    }
  });
  
  // Handle Firebase connection errors
  window.addEventListener('error', (event) => {
    if (event.error?.message?.includes('firestore') || 
        event.error?.message?.includes('Firestore') ||
        event.error?.message?.includes('400') ||
        event.error?.message?.includes('Bad Request')) {
      console.warn('Firebase connection error caught:', event.error);
      // Don't prevent default for these, just log them
    }
  });
}

/* Utilities (kept from your version) */
export function isFirebaseInitialized() {
  try {
    if (!app?.options) return false;
    if (!db || typeof db !== "object") return false;
    if (!auth || typeof auth.onAuthStateChanged !== "function") return false;
    return true;
  } catch {
    return false;
  }
}

export async function testFirebaseConnection() {
  try {
    if (!isFirebaseInitialized()) throw new Error("Firebase not initialized");
    const _ = doc(db, "test", "connection-test");
    return true;
  } catch {
    return false;
  }
}

// Enhanced error handling for Firestore operations
export function handleFirestoreError(error, context = 'Firestore operation') {
  console.error(`${context} error:`, error);
  
  // Common Firestore error patterns
  if (error.code === 'unavailable') {
    console.warn('Firestore is temporarily unavailable, retrying...');
    return 'Connection temporarily unavailable';
  }
  
  if (error.code === 'permission-denied') {
    console.warn('Permission denied for Firestore operation');
    return 'Permission denied';
  }
  
  if (error.code === 'unauthenticated') {
    console.warn('User authentication required');
    return 'Authentication required';
  }
  
  if (error.message?.includes('400') || error.message?.includes('Bad Request')) {
    console.warn('Bad request to Firestore, possible connection issue');
    return 'Connection issue detected';
  }
  
  return error.message || 'Unknown Firestore error';
}

// Safe Firestore listener wrapper
export function createSafeListener(query, onNext, onError) {
  let isActive = true;
  
  const unsubscribe = onSnapshot(
    query,
    (snapshot) => {
      if (isActive) {
        try {
          onNext(snapshot);
        } catch (error) {
          console.error('Error in Firestore listener callback:', error);
          if (onError) onError(error);
        }
      }
    },
    (error) => {
      if (isActive) {
        console.error('Firestore listener error:', error);
        if (onError) onError(error);
      }
    }
  );
  
  return () => {
    isActive = false;
    unsubscribe();
  };
}

